import { useCallback, useEffect, useMemo, useReducer } from 'react';

import { usePredictionsContext } from '@src/providers/PredictionsProvider';
import { filterEventsByMarketIndex, filterSeriesByMarketIndex } from '@src/providers/predictionRefDataIndexes';
import { getPredictionEventForSecurity } from '@src/types/PredictionMarket';
import {
  CardViewEnum,
  EMPTY_ARRAY,
  useMarketAccountsContext,
  useMarketsContext,
  type IPredictionOutcomeEvent,
  type IPredictionOutcomeEventSeriesRecord,
  type Security,
} from '@talos/kyoko';
import { sortBy } from 'lodash-es';

import {
  filterPredictionOutcomesByMarket,
  getPredictionMarketAccountsForMarket,
  resolveInitialPredictionMarket,
  resolvePredictionMarketAccounts,
} from './predictionCardMarkets';
import { filterPredictionEventsBySeries, resolvePredictionEventSeries } from './predictionCardSeries';
import { sortPredictionOutcomesByDisplaySymbol } from './predictionOutcomeSorting';
import { usePredictionMarketDataCardMarketSelectorItems } from './usePredictionMarketDataCardMarketSelectorItems';

export type PredictionCardFormValues = {
  market: string | undefined;
  eventSeries: IPredictionOutcomeEventSeriesRecord | undefined;
  event: IPredictionOutcomeEvent | undefined;
  outcome: Security | undefined;
  selectedMarketAccounts: string[];
  /** Trading aggregation that produced `selectedMarketAccounts`, when the user picked one. */
  selectedAggregation: string | undefined;
  view: CardViewEnum;
};

export type PredictionCardFormMode = 'create' | 'settings';

function sameStringList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * An aggregation only describes the exact account selection it produced, so any divergence
 * (pruned accounts, a different venue's inventory) drops back to a custom selection.
 */
function keepCompatibleAggregation(
  previousAggregation: string | undefined,
  previousAccounts: readonly string[],
  nextAccounts: readonly string[]
): string | undefined {
  return previousAggregation && sameStringList(previousAccounts, nextAccounts) ? previousAggregation : undefined;
}

/**
 * Keeps the Outcome while some Event still in scope offers it. Taking the whole in-scope Event list
 * (rather than only a selected Event) stops a widening of the scope from dropping the Outcome, so
 * clearing Event then Series behaves the same as clearing Series then Event.
 */
function keepCompatibleOutcome(
  eventsInScope: IPredictionOutcomeEvent[],
  previousOutcome: Security | undefined,
  getOutcomesForEvent: (event: IPredictionOutcomeEvent) => Security[]
): Security | undefined {
  if (!previousOutcome) {
    return undefined;
  }
  for (const event of eventsInScope) {
    const match = getOutcomesForEvent(event).find(candidate => candidate.Symbol === previousOutcome.Symbol);
    if (match) {
      return match;
    }
  }
  return undefined;
}

function keepCompatibleEvent(
  previousEvent: IPredictionOutcomeEvent | undefined,
  availableEvents: IPredictionOutcomeEvent[]
): IPredictionOutcomeEvent | undefined {
  if (!previousEvent) {
    return undefined;
  }
  return availableEvents.some(event => event.EventID === previousEvent.EventID) ? previousEvent : undefined;
}

type PredictionCardFormAction =
  | { type: 'resolveMarket'; supportedMarkets: string[] }
  | { type: 'setMarket'; market: string | undefined }
  | {
      type: 'changeMarket';
      market: string | undefined;
      seriesForMarket: IPredictionOutcomeEventSeriesRecord[];
      eventsForMarket: IPredictionOutcomeEvent[];
      getOutcomesForEvent: (event: IPredictionOutcomeEvent) => Security[];
      resolveMarketAccounts: (previousAccounts: readonly string[], hasEvent: boolean) => string[];
    }
  | { type: 'setEventSeries'; eventSeries: IPredictionOutcomeEventSeriesRecord | undefined }
  | {
      type: 'changeEventSeries';
      eventSeries: IPredictionOutcomeEventSeriesRecord | undefined;
      eventsForSeries: IPredictionOutcomeEvent[];
      getOutcomesForEvent: (event: IPredictionOutcomeEvent) => Security[];
      resolveMarketAccounts: (previousAccounts: readonly string[], hasEvent: boolean) => string[];
    }
  | {
      type: 'changeEvent';
      event: IPredictionOutcomeEvent | undefined;
      backfilledSeries: IPredictionOutcomeEventSeriesRecord | undefined;
      getOutcomesForEvent: (event: IPredictionOutcomeEvent) => Security[];
      resolveMarketAccounts: (previousAccounts: readonly string[], hasEvent: boolean) => string[];
    }
  | {
      type: 'changeOutcome';
      outcome: Security | undefined;
      event?: IPredictionOutcomeEvent;
    }
  | { type: 'changeMarketAccounts'; selectedMarketAccounts: string[]; selectedAggregation: string | undefined }
  | { type: 'resolveMarketAccounts'; selectedMarketAccounts: string[] }
  | { type: 'changeView'; view: CardViewEnum };

export function predictionCardFormReducer(
  state: PredictionCardFormValues,
  action: PredictionCardFormAction
): PredictionCardFormValues {
  switch (action.type) {
    case 'resolveMarket': {
      const market = resolveInitialPredictionMarket(state.market, action.supportedMarkets);
      if (market === state.market) {
        return state;
      }
      return { ...state, market };
    }
    case 'setMarket':
      return { ...state, market: action.market };
    case 'changeMarket': {
      // Keep the previously selected series/event/outcome if they are still applicable to the
      // newly selected market, otherwise reset them.
      const seriesStillValid =
        state.eventSeries && action.seriesForMarket.some(series => series.SeriesID === state.eventSeries?.SeriesID);
      const eventSeries = seriesStillValid ? state.eventSeries : undefined;
      const scopedEvents = filterPredictionEventsBySeries(action.eventsForMarket, eventSeries);
      const event = keepCompatibleEvent(state.event, scopedEvents);
      const outcome = keepCompatibleOutcome(event ? [event] : scopedEvents, state.outcome, action.getOutcomesForEvent);
      const selectedMarketAccounts = action.resolveMarketAccounts(state.selectedMarketAccounts, !!event);
      return {
        ...state,
        market: action.market,
        eventSeries,
        event,
        outcome,
        selectedMarketAccounts,
        selectedAggregation: keepCompatibleAggregation(
          state.selectedAggregation,
          state.selectedMarketAccounts,
          selectedMarketAccounts
        ),
        view: outcome ? CardViewEnum.COMPACT : state.view,
      };
    }
    case 'setEventSeries':
      return { ...state, eventSeries: action.eventSeries };
    case 'changeEventSeries': {
      const event = keepCompatibleEvent(state.event, action.eventsForSeries);
      const outcome = keepCompatibleOutcome(
        event ? [event] : action.eventsForSeries,
        state.outcome,
        action.getOutcomesForEvent
      );
      const selectedMarketAccounts = action.resolveMarketAccounts(state.selectedMarketAccounts, !!event);
      return {
        ...state,
        eventSeries: action.eventSeries,
        event,
        outcome,
        selectedMarketAccounts,
        selectedAggregation: keepCompatibleAggregation(
          state.selectedAggregation,
          state.selectedMarketAccounts,
          selectedMarketAccounts
        ),
        view: outcome ? CardViewEnum.COMPACT : state.view,
      };
    }
    case 'changeEvent': {
      // Clearing Event keeps Outcome selected; switching Event clears Outcome when incompatible.
      if (!action.event) {
        return {
          ...state,
          event: undefined,
          view: state.outcome ? CardViewEnum.COMPACT : state.view,
        };
      }
      const outcome = keepCompatibleOutcome([action.event], state.outcome, action.getOutcomesForEvent);
      const selectedMarketAccounts = action.resolveMarketAccounts(state.selectedMarketAccounts, true);
      return {
        ...state,
        // Selecting an event fills its parent series; clearing event leaves series as-is.
        eventSeries: action.backfilledSeries ?? state.eventSeries,
        event: action.event,
        outcome,
        selectedMarketAccounts,
        selectedAggregation: keepCompatibleAggregation(
          state.selectedAggregation,
          state.selectedMarketAccounts,
          selectedMarketAccounts
        ),
        view: outcome ? CardViewEnum.COMPACT : state.view,
      };
    }
    case 'changeOutcome':
      return {
        ...state,
        outcome: action.outcome,
        ...(action.outcome && action.event ? { event: action.event } : {}),
        view: action.outcome ? CardViewEnum.COMPACT : CardViewEnum.NORMAL,
      };
    case 'changeMarketAccounts':
      if (
        sameStringList(state.selectedMarketAccounts, action.selectedMarketAccounts) &&
        state.selectedAggregation === action.selectedAggregation
      ) {
        return state;
      }
      return {
        ...state,
        selectedMarketAccounts: action.selectedMarketAccounts,
        selectedAggregation: action.selectedAggregation,
      };
    case 'resolveMarketAccounts':
      if (sameStringList(state.selectedMarketAccounts, action.selectedMarketAccounts)) {
        return state;
      }
      return {
        ...state,
        selectedMarketAccounts: action.selectedMarketAccounts,
        selectedAggregation: keepCompatibleAggregation(
          state.selectedAggregation,
          state.selectedMarketAccounts,
          action.selectedMarketAccounts
        ),
      };
    case 'changeView':
      return { ...state, view: action.view };
  }
}

export function usePredictionCardForm(mode: PredictionCardFormMode, initialValues: PredictionCardFormValues) {
  const {
    selectableEvents,
    eventsByID,
    eventSeries,
    eventSeriesByID,
    getOutcomesForPredictionEvent,
    refDataIndexes,
    isRefDataReady,
  } = usePredictionsContext();
  const { marketDisplayNameByName } = useMarketsContext();
  const { marketAccountsByMarket, isMarketAccountActive } = useMarketAccountsContext();

  const [form, dispatch] = useReducer(predictionCardFormReducer, initialValues);

  const supportedMarkets = isRefDataReady ? refDataIndexes.supportedMarkets : EMPTY_ARRAY;

  // Keep market selection valid as supported markets load/change.
  useEffect(() => {
    if (!isRefDataReady || supportedMarkets.length === 0) {
      return;
    }
    dispatch({ type: 'resolveMarket', supportedMarkets });
  }, [form.market, isRefDataReady, supportedMarkets]);

  const showMarketField = supportedMarkets.length > 1;

  const marketOptions = useMemo(
    () =>
      sortBy(
        supportedMarkets.map(name => ({
          name,
          label: marketDisplayNameByName.get(name) ?? name,
        })),
        option => option.label
      ),
    [marketDisplayNameByName, supportedMarkets]
  );

  // Only offer tradable events, but keep a configured non-open event visible in settings.
  const eventSource = useMemo(() => {
    if (mode === 'settings' && form.event && !selectableEvents.some(event => event.EventID === form.event?.EventID)) {
      return [form.event, ...selectableEvents];
    }
    return selectableEvents;
  }, [form.event, mode, selectableEvents]);

  const seriesOptions = useMemo(() => {
    if (!isRefDataReady) {
      return [];
    }
    return filterSeriesByMarketIndex(eventSeries, eventSource, form.market, refDataIndexes, form.eventSeries);
  }, [eventSeries, eventSource, form.eventSeries, form.market, isRefDataReady, refDataIndexes]);

  const eventOptions = useMemo(() => {
    if (!isRefDataReady) {
      return [];
    }
    return filterPredictionEventsBySeries(
      filterEventsByMarketIndex(eventSource, form.market, refDataIndexes, form.event),
      form.eventSeries
    );
  }, [eventSource, form.event, form.eventSeries, form.market, isRefDataReady, refDataIndexes]);

  const outcomeOptions = useMemo(() => {
    if (!isRefDataReady) {
      return [];
    }
    if (form.event) {
      return sortPredictionOutcomesByDisplaySymbol(getOutcomesForPredictionEvent(form.event));
    }
    // Outcome-first: reuse market+series scoped events (same gate as the Event dropdown).
    return sortPredictionOutcomesByDisplaySymbol(
      filterPredictionOutcomesByMarket(eventOptions.flatMap(getOutcomesForPredictionEvent), form.market)
    );
  }, [eventOptions, form.event, form.market, getOutcomesForPredictionEvent, isRefDataReady]);

  const activeMarketAccounts = useMemo(() => {
    if (!form.event) {
      return [];
    }
    return getPredictionMarketAccountsForMarket(form.market, marketAccountsByMarket).filter(isMarketAccountActive);
  }, [form.event, form.market, isMarketAccountActive, marketAccountsByMarket]);
  const marketAccountItems = usePredictionMarketDataCardMarketSelectorItems({
    outcome: form.outcome,
    market: form.market,
    marketAccounts: activeMarketAccounts,
  });
  const marketAccountOptions = useMemo(
    () => marketAccountItems.flatMap(item => (item.type === 'MarketAccount' ? [item.marketAccount] : [])),
    [marketAccountItems]
  );

  // Keep / default Market Accounts once an Event is selected.
  useEffect(() => {
    if (!form.event) {
      if (form.selectedMarketAccounts.length > 0) {
        dispatch({ type: 'resolveMarketAccounts', selectedMarketAccounts: [] });
      }
      return;
    }

    const nextAccounts = resolvePredictionMarketAccounts(form.selectedMarketAccounts, marketAccountOptions);
    dispatch({ type: 'resolveMarketAccounts', selectedMarketAccounts: nextAccounts });
  }, [form.event, form.selectedMarketAccounts, marketAccountOptions]);

  const resolveMarketAccountsForMarket = useCallback(
    (market: string | undefined, previousAccounts: readonly string[], hasEvent: boolean) => {
      if (!hasEvent) {
        return [];
      }
      const options = getPredictionMarketAccountsForMarket(market, marketAccountsByMarket).filter(
        isMarketAccountActive
      );
      return resolvePredictionMarketAccounts(previousAccounts, options);
    },
    [isMarketAccountActive, marketAccountsByMarket]
  );

  const handleMarketChange = useCallback(
    (market: string | undefined) => {
      if (!isRefDataReady) {
        dispatch({ type: 'setMarket', market });
        return;
      }
      // Omit selectedSeries so an incompatible series is not injected and then preserved.
      dispatch({
        type: 'changeMarket',
        market,
        seriesForMarket: filterSeriesByMarketIndex(eventSeries, eventSource, market, refDataIndexes),
        eventsForMarket: filterEventsByMarketIndex(eventSource, market, refDataIndexes),
        getOutcomesForEvent: getOutcomesForPredictionEvent,
        resolveMarketAccounts: (previousAccounts, hasEvent) =>
          resolveMarketAccountsForMarket(market, previousAccounts, hasEvent),
      });
    },
    [
      eventSeries,
      eventSource,
      getOutcomesForPredictionEvent,
      isRefDataReady,
      refDataIndexes,
      resolveMarketAccountsForMarket,
    ]
  );

  const handleEventSeriesChange = useCallback(
    (nextSeries: IPredictionOutcomeEventSeriesRecord | undefined) => {
      if (!isRefDataReady) {
        dispatch({ type: 'setEventSeries', eventSeries: nextSeries });
        return;
      }
      dispatch({
        type: 'changeEventSeries',
        eventSeries: nextSeries,
        eventsForSeries: filterPredictionEventsBySeries(
          filterEventsByMarketIndex(eventSource, form.market, refDataIndexes),
          nextSeries
        ),
        getOutcomesForEvent: getOutcomesForPredictionEvent,
        resolveMarketAccounts: (previousAccounts, hasEvent) =>
          resolveMarketAccountsForMarket(form.market, previousAccounts, hasEvent),
      });
    },
    [
      eventSource,
      form.market,
      getOutcomesForPredictionEvent,
      isRefDataReady,
      refDataIndexes,
      resolveMarketAccountsForMarket,
    ]
  );

  const handleEventChange = useCallback(
    (event: IPredictionOutcomeEvent | undefined) => {
      dispatch({
        type: 'changeEvent',
        event,
        backfilledSeries: resolvePredictionEventSeries(event, eventSeriesByID),
        getOutcomesForEvent: getOutcomesForPredictionEvent,
        resolveMarketAccounts: (previousAccounts, hasEvent) =>
          resolveMarketAccountsForMarket(form.market, previousAccounts, hasEvent),
      });
    },
    [eventSeriesByID, form.market, getOutcomesForPredictionEvent, resolveMarketAccountsForMarket]
  );

  const handleOutcomeChange = useCallback(
    (outcome: Security | undefined) => {
      const event = outcome ? getPredictionEventForSecurity(outcome, eventsByID) : undefined;
      dispatch({ type: 'changeOutcome', outcome, event });
    },
    [eventsByID]
  );

  const handleMarketAccountsChange = useCallback(
    (selectedMarketAccounts: string[], selectedAggregation: string | undefined) => {
      dispatch({ type: 'changeMarketAccounts', selectedMarketAccounts, selectedAggregation });
    },
    []
  );

  const handleViewChange = useCallback((view: CardViewEnum) => {
    dispatch({ type: 'changeView', view });
  }, []);

  return {
    form,
    showMarketField,
    marketOptions,
    seriesOptions,
    eventOptions,
    outcomeOptions,
    marketAccountOptions,
    marketAccountItems,
    isRefDataReady,
    handleMarketChange,
    handleEventSeriesChange,
    handleEventChange,
    handleOutcomeChange,
    handleMarketAccountsChange,
    handleViewChange,
  };
}
